import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import { useEffect, useState } from "react";
import { Tooltip } from "@/components/Common/Tooltip";
import { Copy, PencilIcon, PlusIcon } from "lucide-react";
import { z, ZodObject, ZodRawShape } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";

interface LayoutNameDialogProps {
  type?: "new" | "edit" | "copy";
  layoutName?: string;
  existingLayoutNames: string[];
  onConfirm: (updatedLayoutName: string) => void;
  open?: boolean;
  setOpen?: (arg: boolean) => void;
}

export const LayoutNameDialog = ({
  type = "new",
  layoutName,
  existingLayoutNames,
  onConfirm,
  open,
  setOpen,
}: LayoutNameDialogProps) => {
  const shape: ZodRawShape = {
    name: z
      .string()
      .min(1, { message: "Layout name must exist" })
      .refine((name) => !existingLayoutNames.includes(name), {
        message: "Name already exists. Please choose a different name.",
      }),
  };

  const formSchema: ZodObject<typeof shape> = z.object({
    ...shape,
  });

  const formDefaultValues = {
    name:
      type === "new"
        ? ""
        : type === "edit"
          ? (layoutName ?? "")
          : `Copy of ${layoutName}`,
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  const [isOpen, setIsOpen] = useState(open ?? false);

  useEffect(() => {
    if (setOpen && open !== undefined && open !== isOpen) {
      setIsOpen(open);
      return;
    }
  }, [open, setOpen, setIsOpen]);
  const handleOpenChange = () => {
    if (setOpen) {
      setOpen(!isOpen);
      return;
    }
    setIsOpen(!isOpen);
  };

  const handleSubmit: SubmitHandler<z.infer<ZodObject<ZodRawShape>>> = (
    values
  ) => {
    if (!values.name) return;

    handleOpenChange();
    onConfirm(values.name.trim());
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {open !== undefined ? null : (
        <Tooltip
          message={
            type === "copy"
              ? "Copy this layout"
              : type === "edit"
                ? "Edit this layout"
                : "Create a new layout"
          }
        >
          <DialogTrigger asChild>
            {type === "copy" ? (
              <Button
                disabled={layoutName?.length === 0}
                onClick={handleOpenChange}
                size="lgIcon"
                className={"disabled:bg-zinc-700"}
              >
                <Copy />
              </Button>
            ) : type === "edit" ? (
              <Button
                disabled={layoutName?.length === 0}
                onClick={handleOpenChange}
                size="lgIcon"
                className={"disabled:bg-zinc-700"}
              >
                <PencilIcon />
              </Button>
            ) : (
              <Button
                onClick={handleOpenChange}
                variant={"accent"}
                size={"lgIcon"}
              >
                <PlusIcon />
              </Button>
            )}
          </DialogTrigger>
        </Tooltip>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {type === "copy"
              ? `Duplicating Layout "${layoutName}"`
              : type === "edit"
                ? "Edit Layout Name"
                : "Create New Layout"}
          </DialogTitle>
          <DialogDescription>Enter a name for the layout</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="pb-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Layout Name</FormLabel>
                    <FormControl>
                      <Input
                        disabled={form.formState.isSubmitting}
                        placeholder="Enter layout name"
                        data-form-type="other"
                        type="text"
                        {...field}
                        className="text-primary"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                onClick={handleOpenChange}
                variant={"destructive"}
                type={"button"}
              >
                Cancel
              </Button>
              <Button variant={"accent"}>Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
